import CryptoJS from 'crypto-js';

import { ENV_CONFIG } from '../config/env.config';

/**
 * Login-token decryption, per FRONTEND_ENCRYPTION.md.
 *
 * The `/login` response returns `records.token` AES-256-CBC encrypted; the
 * value is stored and resent unchanged (the backend decrypts it). We only need
 * to decrypt it on the client to read the JWT claims that build the session.
 *
 *   Algorithm : AES-256-CBC / PKCS7
 *   Key       : SHA-256(RESPONSE_ENC_KEY)  (32 bytes)
 *   Layout    : base64( IV[16 bytes] ‖ ciphertext )
 *   Plaintext : the JWT string (header.payload.signature)
 */

/** Shared secret (RESPONSE_ENC_KEY). Loaded from env, never hardcoded. */
const ENC_KEY = ENV_CONFIG.encKey;

/**
 * Decrypt the encrypted login token into the raw JWT string.
 * Returns '' when the key is missing or decryption fails, so callers can fall
 * back to treating the value as an already-plain token.
 */
export function decryptToken(encryptedToken: string): string {
  if (!encryptedToken || !ENC_KEY) return '';

  try {
    const key = CryptoJS.SHA256(ENC_KEY); // 32-byte key

    const raw = CryptoJS.enc.Base64.parse(encryptedToken);
    const words = raw.words;

    // First 16 bytes are the IV; the remainder is the ciphertext.
    const iv = CryptoJS.lib.WordArray.create(words.slice(0, 4), 16);
    const ct = CryptoJS.lib.WordArray.create(words.slice(4), raw.sigBytes - 16);

    const decrypted = CryptoJS.AES.decrypt(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { ciphertext: ct } as any,
      key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    return decrypted.toString(CryptoJS.enc.Utf8); // "eyJ....eyJ....sig"
  } catch {
    return '';
  }
}
